/**
 * pwaDynamic.js — per-institute PWA branding applied at runtime.
 *
 * The app is static-hosted (S3) with one bundled public/manifest.json, so the
 * installed-app icon, splash screen and iOS startup image can't be generated
 * per-tenant on the server. Instead we build them client-side from the institute
 * branding once it resolves:
 *
 *   • Android / desktop  → a Blob manifest (same-origin as the document, so its
 *     start_url/scope pass Chrome's installability check) replaces the static
 *     manifest <link>. Chrome composes the splash from this manifest's icon +
 *     background_color + name automatically.
 *   • iOS Safari standalone → a canvas-rendered apple-touch-startup-image sized
 *     to the current device (iOS has no auto-generated splash).
 *   • apple-touch-icon → the institute's PWA icon.
 *
 * SAFETY: everything is gated on the institute actually having a real http(s)
 * icon. With no custom branding (or an enterprise data:-URI default) we touch
 * nothing, so the bundled manifest.json / default install keeps working exactly
 * as before. Failures are swallowed — branding must never break the app.
 */

const isHttpUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u);

const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS 13+ reports as Mac; disambiguate via touch points
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Resolve which asset to use, newest-intent first, falling back through the
// other branding images the institute may have uploaded.
const pickIcon = (b) => b.pwaIconUrl || b.faviconUrl || b.logoUrl || null;
const pickSplashLogo = (b) => b.pwaSplashUrl || b.pwaIconUrl || b.logoUrl || b.faviconUrl || null;

let _manifestBlobUrl = null;
let _lastKey = null;

/**
 * Build + install a Blob web-app manifest from branding.
 * Returns true if a dynamic manifest was applied, false if skipped.
 */
function applyDynamicManifest(branding, iconUrl) {
  try {
    const origin = window.location.origin;
    const name = branding.brandName || 'App';
    const themeColor = branding.primaryColor || '#2563eb';
    const bgColor = branding.logoBgColor || '#ffffff';

    const manifest = {
      name,
      short_name: name.length > 12 ? name.slice(0, 12).trim() : name,
      // Absolute + same-origin as the document so the Blob manifest (whose origin
      // equals the page origin) satisfies Chrome's "start_url same-origin" rule.
      id: `${origin}/`,
      start_url: `${origin}/`,
      scope: `${origin}/`,
      display: 'standalone',
      // No `orientation` key on purpose — the installed app then follows the
      // device's rotation-lock instead of force-rotating.
      background_color: bgColor,
      theme_color: themeColor,
      icons: [
        { src: iconUrl, sizes: '192x192', purpose: 'any' },
        { src: iconUrl, sizes: '512x512', purpose: 'any' },
        { src: iconUrl, sizes: '512x512', purpose: 'maskable' },
      ],
    };

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
    const url = URL.createObjectURL(blob);

    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);

    if (_manifestBlobUrl) URL.revokeObjectURL(_manifestBlobUrl);
    _manifestBlobUrl = url;
    return true;
  } catch (err) {
    // Leave the static manifest in place on any failure.
    return false;
  }
}

/** Point apple-touch-icon (iOS home-screen icon) at the institute PWA icon. */
function applyAppleTouchIcon(iconUrl) {
  try {
    let link = document.querySelector('link[rel="apple-touch-icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'apple-touch-icon';
      document.head.appendChild(link);
    }
    link.href = iconUrl;
  } catch {}
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // needed so the canvas isn't tainted on export
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Render a full-screen splash to a PNG data URL for the current device.
 * `withLogo:false` skips the (possibly cross-origin) image so the canvas is
 * never tainted — used as the fallback when toDataURL() throws.
 */
async function renderSplash({ logoUrl, bgColor, brandName, withLogo }) {
  const dpr = window.devicePixelRatio || 2;
  const w = Math.round(window.screen.width * dpr);
  const h = Math.round(window.screen.height * dpr);
  if (!w || !h) return null;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = bgColor || '#ffffff';
  ctx.fillRect(0, 0, w, h);

  let drewLogo = false;
  if (withLogo && isHttpUrl(logoUrl)) {
    try {
      const img = await loadImage(logoUrl);
      const maxW = w * 0.45;
      const maxH = h * 0.3;
      const scale = Math.min(maxW / img.width, maxH / img.height, dpr);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      drewLogo = true;
    } catch {
      // fall through to text
    }
  }

  if (!drewLogo && brandName) {
    ctx.fillStyle = isLightColor(bgColor) ? '#111111' : '#ffffff';
    // Open Sans first, with the same system fallbacks --font-sans carries. A
    // canvas draws with whatever is loaded at the moment fillText runs, so if
    // the webfont has not arrived the splash falls back rather than blocking.
    ctx.font = `600 ${Math.round(36 * dpr)}px 'Open Sans', system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(brandName, w / 2, h / 2);
  }

  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null; // tainted canvas
  }
}

function isLightColor(hex) {
  if (typeof hex !== 'string') return true;
  const m = hex.replace('#', '');
  if (m.length < 6) return true;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  // perceived luminance
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}

async function applyIosSplash({ logoUrl, bgColor, brandName }) {
  try {
    // Prefer bg + logo; if the cross-origin logo taints the canvas, fall back to
    // a bg + brand-name splash (never tainted) so iOS still gets a branded screen.
    let dataUrl = await renderSplash({ logoUrl, bgColor, brandName, withLogo: true });
    if (!dataUrl) {
      dataUrl = await renderSplash({ logoUrl, bgColor, brandName, withLogo: false });
    }
    if (!dataUrl) return;

    const w = window.screen.width;
    const h = window.screen.height;
    const dpr = window.devicePixelRatio || 2;

    document
      .querySelectorAll('link[rel="apple-touch-startup-image"][data-dynamic="1"]')
      .forEach((l) => l.remove());

    const link = document.createElement('link');
    link.rel = 'apple-touch-startup-image';
    link.setAttribute('data-dynamic', '1');
    link.media = `(device-width: ${w}px) and (device-height: ${h}px) and (-webkit-device-pixel-ratio: ${dpr})`;
    link.href = dataUrl;
    document.head.appendChild(link);
  } catch {}
}

/**
 * Apply per-institute PWA branding (manifest icon/splash/colors + iOS splash).
 * Call whenever branding resolves; no-ops when there's no real http(s) icon, so
 * the bundled static manifest and default install are left untouched.
 */
export function applyDynamicPwaBranding(branding) {
  if (!branding || typeof document === 'undefined') return;

  const iconUrl = pickIcon(branding);
  // Skip for the default (no-branding) and enterprise data:-URI cases — the
  // bundled manifest.json already covers those and its PNG icons are better
  // installability candidates than a data: SVG.
  if (!isHttpUrl(iconUrl)) return;

  const splashLogoUrl = pickSplashLogo(branding);
  const bgColor = branding.logoBgColor || '#ffffff';

  // De-dupe: BrandingContext applies cached branding on mount and again after a
  // background refresh — skip the (canvas) work when nothing relevant changed.
  const key = [iconUrl, splashLogoUrl, branding.brandName, branding.primaryColor, bgColor].join('|');
  if (key === _lastKey) return;
  _lastKey = key;

  applyAppleTouchIcon(iconUrl);
  applyDynamicManifest(branding, iconUrl);

  if (isIOS()) {
    applyIosSplash({ logoUrl: splashLogoUrl, bgColor, brandName: branding.brandName });
  }
}

export default applyDynamicPwaBranding;
